import { useMemo } from "react";

import type { FeatureFlagsDataEnvelope } from "../../../../models/featureFlags";
import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../../services/query";

import { useFeatureFlagsQuery } from "../featureFlags.query";

export * from "./fabricFeatureFlags.static";

export const getFabricFeatureFlagsAccessError = ({
  ignoreUserId = false,
  ignoreOperationId = false,
}: {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
}) => {
  let modifiersString = "";
  if (ignoreUserId && ignoreOperationId) {
    modifiersString = " without userId and operationId";
  } else if (ignoreUserId) {
    modifiersString = " without userId";
  } else if (ignoreOperationId) {
    modifiersString = " without operationId";
  }

  return new Error(
    `Attempted to access fabric feature flags data${modifiersString} but it is not available`,
  );
};

const app = "fabric";

// #################################
// ## useFabricFeatureFlagsStatus ##
// #################################

export type ParamsOfUseFabricFeatureFlagsStatus = {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseFabricFeatureFlagsStatus = QueryStatus;
export type UseFabricFeatureFlagsStatus = (
  params?: ParamsOfUseFabricFeatureFlagsStatus,
) => ResultOfUseFabricFeatureFlagsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of fabric feature flags data.
 *
 * This takes into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flags will not target any user.
 * Otherwise, the feature flags will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flags will not target any operation.
 * Otherwise, the feature flags will target the current `operationId`.
 */
export const useFabricFeatureFlagsStatus: UseFabricFeatureFlagsStatus = ({
  ignoreUserId = false,
  ignoreOperationId = false,
} = {}) => {
  return useFeatureFlagsQuery({
    app,
    ignoreUserId,
    ignoreOperationId,
    watch: ["status"],
  }).status;
};

// ################################
// ## useFabricFeatureFlagsError ##
// ################################

export type ParamsOfUseFabricFeatureFlagsError = {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseFabricFeatureFlagsError = Error | null;
export type UseFabricFeatureFlagsError = (
  params?: ParamsOfUseFabricFeatureFlagsError,
) => ResultOfUseFabricFeatureFlagsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of fabric feature flags data
 * or null if no error occurred.
 *
 * This takes into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flags will not target any user.
 * Otherwise, the feature flags will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flags will not target any operation.
 * Otherwise, the feature flags will target the current `operationId`.
 */
export const useFabricFeatureFlagsError: UseFabricFeatureFlagsError = ({
  ignoreUserId = false,
  ignoreOperationId = false,
} = {}) => {
  return useFeatureFlagsQuery({
    app,
    ignoreUserId,
    ignoreOperationId,
    watch: ["error"],
  }).error;
};

// ####################################
// ## useMaybeFabricFeatureFlagsData ##
// ####################################

export type ParamsOfUseMaybeFabricFeatureFlagsData = {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseMaybeFabricFeatureFlagsData =
  | FeatureFlagsDataEnvelope
  | undefined;
export type UseMaybeFabricFeatureFlagsData = (
  params?: ParamsOfUseMaybeFabricFeatureFlagsData,
) => ResultOfUseMaybeFabricFeatureFlagsData;

/**
 * The returned value of this hook will contain
 * fabric feature flags data, which may be undefined.
 *
 * This takes into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flags will not target any user.
 * Otherwise, the feature flags will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flags will not target any operation.
 * Otherwise, the feature flags will target the current `operationId`.
 */
export const useMaybeFabricFeatureFlagsData: UseMaybeFabricFeatureFlagsData = ({
  ignoreUserId = false,
  ignoreOperationId = false,
} = {}) => {
  return useFeatureFlagsQuery({
    app,
    ignoreUserId,
    ignoreOperationId,
    watch: ["data"],
  }).data;
};

// ###############################
// ## useFabricFeatureFlagsData ##
// ###############################

export type ParamsOfUseFabricFeatureFlagsData = {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseFabricFeatureFlagsData = FeatureFlagsDataEnvelope;
export type UseFabricFeatureFlagsData = (
  params?: ParamsOfUseFabricFeatureFlagsData,
) => ResultOfUseFabricFeatureFlagsData;

/**
 * The returned value of this hook will contain fabric feature flags data.
 *
 * This takes into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flags will not target any user.
 * Otherwise, the feature flags will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flags will not target any operation.
 * Otherwise, the feature flags will target the current `operationId`.
 *
 * This hook is guaranteed to contain fabric feature flags data or will throw.
 */
export const useFabricFeatureFlagsData: UseFabricFeatureFlagsData = ({
  ignoreUserId = false,
  ignoreOperationId = false,
} = {}) => {
  const data = useMaybeFabricFeatureFlagsData({
    ignoreUserId,
    ignoreOperationId,
  });
  if (data === undefined) {
    throw getFabricFeatureFlagsAccessError({ ignoreUserId, ignoreOperationId });
  }
  return data;
};

// ################################
// ## useMaybeFabricFeatureFlags ##
// ################################

export type ParamsOfUseMaybeFabricFeatureFlags = {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseMaybeFabricFeatureFlags = QueryEnvelope<
  FeatureFlagsDataEnvelope,
  Error
>;
export type UseMaybeFabricFeatureFlags = (
  params?: ParamsOfUseMaybeFabricFeatureFlags,
) => ResultOfUseMaybeFabricFeatureFlags;

/**
 * The `data` property returned from this hook will contain
 * fabric feature flags data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of fabric feature flags data.
 *
 * This hook takes into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flags will not target any user.
 * Otherwise, the feature flags will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flags will not target any operation.
 * Otherwise, the feature flags will target the current `operationId`.
 */
export const useMaybeFabricFeatureFlags: UseMaybeFabricFeatureFlags = ({
  ignoreUserId = false,
  ignoreOperationId = false,
} = {}) => {
  const { status, error, data } = useFeatureFlagsQuery({
    app,
    ignoreUserId,
    ignoreOperationId,
    watch: ["status", "error", "data"],
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ###########################
// ## useFabricFeatureFlags ##
// ###########################

export type ParamsOfUseFabricFeatureFlags = {
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
};
export type ResultOfUseFabricFeatureFlags = EnsuredQueryEnvelope<
  FeatureFlagsDataEnvelope,
  Error
>;
export type UseFabricFeatureFlags = (
  params?: ParamsOfUseFabricFeatureFlags,
) => ResultOfUseFabricFeatureFlags;

/**
 * The `data` property returned from this hook will contain
 * fabric feature flags data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of fabric feature flags data.
 *
 * This hook takes into account the targeting of `userId`, and `operationId`.
 *
 * If `ignoreUserId`, the feature flags will not target any user.
 * Otherwise, the feature flags will target the current `userId`.
 *
 * If `ignoreOperationId`, the feature flags will not target any operation.
 * Otherwise, the feature flags will target the current `operationId`.
 *
 * This hook is guaranteed to contain fabric feature flags data or will throw.
 */
export const useFabricFeatureFlags: UseFabricFeatureFlags = ({
  ignoreUserId = false,
  ignoreOperationId = false,
} = {}) => {
  const { status, error, data } = useFeatureFlagsQuery({
    app,
    ignoreUserId,
    ignoreOperationId,
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw getFabricFeatureFlagsAccessError({
      ignoreUserId,
      ignoreOperationId,
    });
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
